import { FC, useEffect, useMemo, useState } from 'react';
import {
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Tooltip
} from '@mui/material';

import {
  CloseButton,
  FaIcon,
  If,
  MDBox,
  MDButton,
  MDTypography
} from '@components/index';
import {
  useLazyGetProjectByIdQuery,
  useUpdateProjectMutation
} from '@api/projects';
import { useDispatch, useSelector } from 'react-redux';
import { setCurrentProjectData } from '@pages/ProjectBoard/slice';
import { MSG_EVENT, MSG_TYPE, NotificationData } from '@DStypes/notifications';
import dayjs from 'dayjs';
import { setUserNotifications } from '@components/Notification/slice';
import {
  useLazyGetChartByIdForInsightsQuery,
  useUpdateChartInsightsMutation,
  useUpdateChartMutation
} from '@api/chart';
import {
  setChartInsights,
  setChartShortDescription
} from '@pages/EditChart/slice';
import { RootState } from '@store/store';
import { sendAmplitudeData } from '@utils/amplitude';
import {
  CLICKED_ADD_AS_DESCRIPTION,
  CLICKED_INSIGHT_DOWNVOTE,
  CLICKED_INSIGHT_UPVOTE,
  REMOVED_VOTE
} from '@constants/eventNames';
import { INSIGHT_CREATION_FAILED } from '@constants/messages';

interface InsightType {
  cfd: number;
  derived_from: string;
  finding: string;
  rel: number;
  added_to_desc?: boolean;
  rating?: number;
}

interface Props {
  open: boolean;
  onClose: () => void;
  insights: string;
  projectId?: string;
  chartId?: string;
  currentDesc: string;
  isChartInsights?: boolean;
}

const InsightsModal: FC<Props> = ({
  open,
  onClose,
  insights,
  projectId,
  currentDesc,
  chartId,
  isChartInsights
}) => {
  const dispatch = useDispatch();
  const currentChartData = useSelector(
    (state: RootState) => state?.currentChartData
  );
  const userDetails = useSelector((state: RootState) => state?.userDetails);
  const currentPlanFeatures = useSelector(
    (state: RootState) => state?.AppData.currentPlanFeatures
  );

  const [updateProject] = useUpdateProjectMutation();
  const [updateChartInsights] = useUpdateChartInsightsMutation();
  const [updateChartShortDesc] = useUpdateChartMutation();

  const [insightsArray, setInsightsArray] = useState(JSON.parse(insights));
  const [selectedInsights, setSelectedInsights] = useState<InsightType[]>([]);
  const [insightCreationFailed, setInsightCreationFailed] = useState(false);

  const [pollingInterval, setPollingInterval] = useState(0);

  const onUpdateProjectDetails = (updatedInsights: any) => {
    if (isChartInsights && chartId) {
      updateChartInsights({ chart_id: chartId, payload: updatedInsights });
      dispatch(setChartInsights({ ...updatedInsights }));
    } else {
      updateProject({ id: projectId, payload: updatedInsights });
      dispatch(setCurrentProjectData({ ...updatedInsights }));
    }
  };
  const [getProjectById] = useLazyGetProjectByIdQuery(
    pollingInterval
      ? {
          pollingInterval: pollingInterval,
          selectFromResult: (response) => {
            if (
              typeof response?.data?.project_insights === 'string' &&
              response?.data?.project_insights !== '' &&
              pollingInterval
            )
              if (
                response?.data?.project_insights === INSIGHT_CREATION_FAILED
              ) {
                setInsightCreationFailed(true);
                setPollingInterval(0);
              } else {
                setInsightsArray(JSON.parse(response?.data?.project_insights));
                setPollingInterval(0);
              }

            return response;
          }
        }
      : {}
  );

  const [getChartById] = useLazyGetChartByIdForInsightsQuery(
    pollingInterval
      ? {
          pollingInterval: pollingInterval,
          selectFromResult: (response) => {
            if (
              typeof response?.data?.chart_insights?.insights === 'string' &&
              response?.data?.chart_insights?.insights !== '' &&
              pollingInterval
            ) {
              setInsightsArray(
                JSON.parse(response?.data?.chart_insights?.insights)
              );
              setPollingInterval(0);
            }

            return response;
          }
        }
      : {}
  );

  useEffect(() => {
    if (JSON.parse(insights)?.length === 0)
      if (isChartInsights) {
        getChartById(chartId);
        setPollingInterval(500);
      } else {
        getProjectById({
          projectId: projectId
        });
        setPollingInterval(500);
      }
  }, [insights]);

  const sendNotification = (msg_event: MSG_EVENT, message: string) => {
    const payload: NotificationData = {
      created_at: dayjs(new Date())
        .utc()
        .format('YYYY-MM-DDTHH:mm:ss.SSS[000][Z]'),
      id: dayjs(new Date()).utc().format('YYYY-MM-DDTHH:mm:ss.SSS[000][Z]'),
      is_read: false,
      is_silent: true,
      is_user_message: true,
      message_event: msg_event,
      message_text: message,
      updated_at: dayjs(new Date())
        .utc()
        .format('YYYY-MM-DDTHH:mm:ss.SSS[000][Z]'),
      message_type: MSG_TYPE.SUCCESS,
      message_data: JSON.stringify({
        project: { id: projectId }
      }),
      linkKeyword: 'here.',
      replaceKeyword: '<click here>',
      is_important: false,
      recipient_obj: 0,
      isAnchorElement: true
    };

    dispatch(setUserNotifications(payload));
  };

  const handleRatingClick = (finding: string, newRating: number) => {
    const index = insightsArray.findIndex(
      (obj: InsightType) => obj.finding === finding
    );

    if (index !== -1) {
      const updatedInsights = [...insightsArray];

      updatedInsights[index] = {
        ...updatedInsights[index],
        rating: newRating
      };

      setInsightsArray(updatedInsights);
      const key = isChartInsights ? 'insights' : 'project_insights';

      onUpdateProjectDetails({
        [key]: JSON.stringify(updatedInsights)
      });
    }
  };

  const handleCheckBoxClick = (insight: InsightType, value: boolean) => {
    if (value) setSelectedInsights([...selectedInsights, insight]);
    else
      setSelectedInsights(selectedInsights.filter((item) => item !== insight));
  };

  const handleAddToDescriptionClick = () => {
    const newDescription =
      selectedInsights.map((insight: InsightType) => insight.finding).join(' ') +
      (currentDesc ? currentDesc : '');

    const updatedInsights = insightsArray.map((insight: InsightType) => {
      const index = selectedInsights.findIndex(
        (obj: InsightType) => obj.finding === insight.finding
      );

      if (index === -1) return insight;
      else return { ...insight, added_to_desc: true };
    });

    setInsightsArray(updatedInsights);

    sendNotification(
      MSG_EVENT.INSIGHT_ADDED_AS_DESCRIPTION,
      `Insight${selectedInsights.length > 1 ? 's' : ''} successfully added to ${
        isChartInsights ? 'chart' : 'project'
      } description`
    );

    sendAmplitudeData(CLICKED_ADD_AS_DESCRIPTION, {
      type: isChartInsights ? 'Chart' : 'Project',
      number_of_insights: selectedInsights?.length
    });

    if (isChartInsights) {
      onUpdateProjectDetails({
        insights: JSON.stringify(updatedInsights)
      });
      dispatch(setChartShortDescription({ chart_short_desc: newDescription }));
      updateChartShortDesc({
        id: chartId,
        payload: {
          chart_title: currentChartData?.chart_title,
          chart_long_desc: currentChartData?.chart_long_desc,
          rating: currentChartData?.rating,
          chart_short_desc: newDescription
        }
      });
    } else {
      onUpdateProjectDetails({
        project_short_desc: newDescription,
        project_insights: JSON.stringify(updatedInsights)
      });
    }

    handleModalClose();
  };

  const handleModalClose = () => {
    setInsightsArray([]);
    setSelectedInsights([]);
    onClose();
  };

  const remainingCredits = useMemo(() => {
    const insightsLimit = currentPlanFeatures?.insightsLimit;

    const insightsLimitLeft = insightsLimit
      ? `${userDetails?.insights_limit_remaining || 0}/${insightsLimit}`
      : '';

    return insightsLimitLeft;
  }, [userDetails, currentPlanFeatures]);

  return (
    <Dialog
      open={open}
      onClose={handleModalClose}
      sx={{
        zIndex: 100,
        '& .MuiPaper-root': {
          width: 'fit-content',
          maxWidth: '80%',
          position: 'relative',
          overflow: 'visible'
        }
      }}
    >
      <CloseButton onClose={() => handleModalClose()} />
      <Tooltip
        title={remainingCredits + ' remaining'}
        PopperProps={{ sx: { zIndex: 6000 } }}
      >
        <MDTypography
          variant='caption'
          sx={{ position: 'absolute', top: 10, right: 15 }}
        >
          {remainingCredits}
        </MDTypography>
      </Tooltip>
      <DialogTitle sx={{ my: 2 }} {...({ component: 'div' } as any)}>
        <MDTypography
          variant='h3'
          color='secondary'
          textGradient
          sx={{ whiteSpace: 'wrap' }}
        >
          {isChartInsights ? 'Chart Insights' : 'Project Insights'}
        </MDTypography>
      </DialogTitle>
      <DialogContent>
        <If condition={insightCreationFailed}>
          <Grid
            container
            display='flex'
            flexDirection='column'
            alignItems='center'
            justifyContent='center'
            gap={3}
            mb={2}
            sx={{ minWidth: '900px', minHeight: '15vh' }}
          >
            <MDTypography variant='h5' color='dark'>
              Sorry, we could not process your request at the moment. Please try
              later.
            </MDTypography>
          </Grid>
        </If>
        <If condition={insightsArray?.length === 0 && !insightCreationFailed}>
          <Grid
            container
            display='flex'
            flexDirection='column'
            alignItems='center'
            gap={3}
            mb={2}
            sx={{ minWidth: '900px' }}
          >
            <MDTypography variant='h5' color='dark'>
              Creating insights is in progress
            </MDTypography>
            <CircularProgress color='secondary' />
            <MDTypography variant='xs'>
              This may take upto a minute. Avoid refreshing or cancelling. Thank
              you!
            </MDTypography>
          </Grid>
        </If>
        <If condition={insightsArray?.length}>
          <Grid container display='flex' flexDirection='column' gap={2} mb={2}>
            {insightsArray.map((insight: InsightType, index: number) => (
              <Grid
                container
                display='flex'
                flexDirection='row'
                flexWrap='nowrap'
                key={index}
                justifyContent='space-between'
                alignItems='center'
              >
                <MDBox
                  display='flex'
                  flexDirection='row'
                  mr={10}
                  alignItems='center'
                >
                  <Tooltip
                    title={
                      insight?.added_to_desc
                        ? `Added to ${
                            isChartInsights ? 'Chart' : 'Project'
                          } Description already`
                        : ''
                    }
                  >
                    <Checkbox
                      sx={{ marginLeft: '11px' }}
                      disabled={insight.rating === -1}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        handleCheckBoxClick(insight, event.target.checked)
                      }
                      checked={insight.added_to_desc}
                    />
                  </Tooltip>
                  <MDTypography
                    variant='h5'
                    color='info'
                    sx={{
                      textDecoration:
                        insight.rating === -1 ? 'line-through' : '',
                      opacity: insight.rating === -1 ? 0.5 : 1
                    }}
                    fontWeight={
                      selectedInsights.findIndex(
                        (obj: InsightType) => obj.finding === insight.finding
                      ) !== -1 || insight.added_to_desc
                        ? 'bold'
                        : ''
                    }
                  >
                    {insight.finding}
                  </MDTypography>
                </MDBox>
                <MDBox display='flex' flexDirection='row' flexWrap='nowrap'>
                  <IconButton
                    color='secondary'
                    onClick={() => {
                      if (insight.rating === 1)
                        sendAmplitudeData(REMOVED_VOTE, {
                          Rating: 'upvote',
                          Type: 'Insight'
                        });
                      else
                        sendAmplitudeData(CLICKED_INSIGHT_UPVOTE, {
                          type: isChartInsights ? 'Chart' : 'Project'
                        });
                      handleRatingClick(
                        insight.finding,
                        insight.rating === 1 ? 0 : 1
                      );
                    }}
                  >
                    <FaIcon
                      icon={[
                        `${insight.rating === 1 ? 'fas' : 'far'}`,
                        'thumbs-up'
                      ]}
                      typographyProps={{
                        variant: 'xl',
                        color: 'success'
                      }}
                    />
                  </IconButton>
                  <IconButton
                    color='secondary'
                    onClick={() => {
                      if (insight.rating === -1)
                        sendAmplitudeData(REMOVED_VOTE, {
                          Rating: 'downvote',
                          Type: 'Insight'
                        });
                      else
                        sendAmplitudeData(CLICKED_INSIGHT_DOWNVOTE, {
                          type: isChartInsights ? 'Chart' : 'Project'
                        });
                      handleRatingClick(
                        insight.finding,
                        insight.rating === -1 ? 0 : -1
                      );
                    }}
                  >
                    <FaIcon
                      icon={[
                        `${insight.rating === -1 ? 'fas' : 'far'}`,
                        'thumbs-down'
                      ]}
                      typographyProps={{
                        variant: 'xl',
                        color: 'error'
                      }}
                    />
                  </IconButton>
                </MDBox>
              </Grid>
            ))}
          </Grid>
        </If>
      </DialogContent>
      <If condition={insightsArray?.length}>
        <DialogActions
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            mb: 3,
            alignItems: 'flex-start'
          }}
        >
          <MDBox
            display='flex'
            flexDirection='column'
            alignItems='center'
            mr={1}
            rowGap={1}
          >
            <MDButton
              color='light'
              size='small'
              disabled={!selectedInsights.length}
              onClick={handleAddToDescriptionClick}
            >
              <MDTypography
                textTransform='capitalize'
                variant='sm'
                color='info'
                fontWeight='bold'
              >
                ADD AS DESCRIPTION
              </MDTypography>
            </MDButton>
          </MDBox>
        </DialogActions>
      </If>
    </Dialog>
  );
};

export default InsightsModal;
